"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateOrgAddressRequest,
  OrgAddressBulkRequest,
  UpdateOrgAddressRequest,
} from "@chotca/config";
import { getSdk } from "../sdk";
import { qk } from "../query-keys";

export function useOrgAddresses(orgId: string | null) {
  return useQuery({
    queryKey: qk.orgAddresses.list(orgId ?? ""),
    queryFn: () => getSdk().orgAddresses.list(orgId as string),
    enabled: !!orgId,
  });
}

export function useCreateOrgAddress(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateOrgAddressRequest) =>
      getSdk().orgAddresses.create(orgId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.orgAddresses.list(orgId) });
    },
  });
}

export function useBulkCreateOrgAddresses(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: OrgAddressBulkRequest) =>
      getSdk().orgAddresses.createBulk(orgId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.orgAddresses.list(orgId) });
    },
  });
}

export function useUpdateOrgAddress(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateOrgAddressRequest }) =>
      getSdk().orgAddresses.update(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.orgAddresses.list(orgId) });
    },
  });
}

export function useArchiveOrgAddress(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => getSdk().orgAddresses.archive(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.orgAddresses.list(orgId) });
    },
  });
}
